import type { BlotterProcessedRow } from './types';
import type { ICellRendererParams } from '@ag-grid-community/core';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'src/components/ui';
import { useSnackbar } from 'src/hooks/useSnackbar';
import { cancelOrder } from 'src/lib/orders';
import { snackbarErrorData } from 'src/lib/util/snackbarErrorData';
import { container } from 'src/StaticContainer';
import { v4 } from 'uuid';

interface IMessageCellRendererParams extends ICellRendererParams {
    data: BlotterProcessedRow;
}

const log = container.get('Logger').getSubLogger({ name: 'order-blotter-actions' }, { component: 'trading' });

export const ActionsCellRenderer = (props: IMessageCellRendererParams) => {
    const canCancel = props.value.includes('cancel');
    const id = props.data.id;
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const { mutate: cancelOrderMutation } = useMutation({
        mutationFn: cancelOrder,
        onSuccess: (_data, variables) => {
            enqueueSnackbar(
                t('widgets::order-blotter::actions::cancel-order-success'),
                { variant: 'success' },
                { requestId: variables.requestId },
            );
            log.info({ message: 'Order cancellation succeeded.', requestId: variables.requestId });
        },
        onError: (error) => {
            const { options, responseData } = snackbarErrorData(error);
            enqueueSnackbar(t('widgets::order-blotter::actions::cancel-order-error'), options, responseData);
            log.error({ message: 'Order cancellation failed. Check network response.' });
        },
    });

    const onClick = useCallback(() => {
        const requestId = v4();
        cancelOrderMutation({ orderId: id, requestId });
    }, [id, cancelOrderMutation]);

    if (TRACE) {
        log.trace({ message: 'Rendering ActionsCellRenderer', canCancel, id });
    }

    if (!canCancel) {
        return null;
    }

    return (
        <Tooltip content="Cancel order">
            <FontAwesomeIcon icon={faTimes} onClick={onClick} className="hover:text-destructive" />
        </Tooltip>
    );
};
